import dayjs, { type Dayjs } from 'dayjs'
import { forEach, isString, pipe } from 'remeda'
import { type Cleanup, composeCleanup } from '../helpers/cleanup'
import { bindLiveProps, type LivePropValues } from '../helpers/live-props'
import {
  combineDateTimeValue,
  isTimeStepAligned,
  isTimeWithinBounds,
  isWithinBounds,
  matchingDateTimeBound,
  normalizeTime,
  preserveTimeMilliseconds,
  splitDateTimeValue,
  type TemporalPrecision,
  temporalPrecision
} from '../helpers/temporal'
import {
  loadPageComponents,
  setupPageComponent
} from '../helpers/webawesome-components'

export {
  combineDateTimeValue,
  preserveTimeMilliseconds,
  splitDateTimeValue
} from '../helpers/temporal'

// Private types ---------------------------------------------------------------

type TemporalInput = HTMLElement & {
  value: string
  min: string
  max: string
  required: boolean
  disabled: boolean
  readonly: boolean
  hide?: () => Promise<void>
  setCustomValidity?: (message: string) => void
}

type DatePicker = HTMLElement & {
  value: string
  min: string
  max: string
  disabled: boolean
  readonly: boolean
  focus: (options?: FocusOptions) => void
}

type DateTimeDisplay = TemporalInput & {
  step: number | 'any'
  withClear: boolean
}

type DateTimePopover = HTMLElement & {
  anchor: Element | null
  open: boolean
  show: () => Promise<void>
  hide: () => Promise<void>
  updateComplete: Promise<boolean>
}

type ValueElement = HTMLElement & {
  value: string
}

type ConstraintElement = HTMLElement & {
  min: string
  max: string
}

type DateTimeHookState = Readonly<{
  cleanup: Cleanup
  sync: () => void
}>

type DateTimeHook = {
  readonly el: HTMLElement
  dateTimeState?: DateTimeHookState
  dateTimeCleanup?: Cleanup
}

// Public types ----------------------------------------------------------------

export type DateTimePrecision = TemporalPrecision

export type DateTimeMacro =
  | '-2hr'
  | '-1hr'
  | '-5min'
  | 'now'
  | '+5min'
  | '+1hr'
  | '+2hr'
  | 'tomorrow'
  | 'next-monday'
  | 'next-month'

// Constants -------------------------------------------------------------------

const RANGE_ERROR = 'Value is outside the allowed range'

const macroTransforms = {
  '-2hr': (date: Dayjs) => date.subtract(2, 'hours'),
  '-1hr': (date: Dayjs) => date.subtract(1, 'hour'),
  '-5min': (date: Dayjs) => date.subtract(5, 'minutes'),
  now: (date: Dayjs) => date,
  '+5min': (date: Dayjs) => date.add(5, 'minutes'),
  '+1hr': (date: Dayjs) => date.add(1, 'hour'),
  '+2hr': (date: Dayjs) => date.add(2, 'hours'),
  tomorrow: (date: Dayjs) => date.add(1, 'day'),
  'next-monday': (date: Dayjs) => date.add((8 - date.day()) % 7 || 7, 'days'),
  'next-month': (date: Dayjs) => date.add(1, 'month')
} satisfies Readonly<Record<DateTimeMacro, (date: Dayjs) => Dayjs>>

const PRECISION_FORMATS = {
  minute: 'YYYY-MM-DDTHH:mm',
  second: 'YYYY-MM-DDTHH:mm:ss',
  millisecond: 'YYYY-MM-DDTHH:mm:ss.SSS'
} as const satisfies Readonly<Record<DateTimePrecision, string>>

const MIDNIGHT_VALUES = {
  minute: '00:00',
  second: '00:00:00',
  millisecond: '00:00:00.000'
} as const satisfies Readonly<Record<DateTimePrecision, string>>

// Private functions -----------------------------------------------------------

// Reads the rendered precision with the shared temporal fallback.
const readPrecision = (element: HTMLElement): DateTimePrecision =>
  temporalPrecision(element.dataset.precision)

// Narrows a string to one supported datetime macro.
const isDateTimeMacro = (value: string): value is DateTimeMacro =>
  value in macroTransforms

// Applies one macro and formats the result at the requested precision.
const applyMacro = (
  value: string,
  macro: DateTimeMacro,
  precision: DateTimePrecision,
  fallback: Dayjs = dayjs()
): string => {
  const parsed = value === '' ? fallback : dayjs(value)
  const base = parsed.isValid() ? parsed : fallback

  return macroTransforms[macro](base).format(PRECISION_FORMATS[precision])
}

// Finds one required related element and validates its runtime API.
const requiredElement = <ElementType extends HTMLElement>(
  root: HTMLElement,
  id: string | undefined,
  predicate: (element: HTMLElement) => element is ElementType
): ElementType | null => {
  const element = id ? document.getElementById(id) : null

  return element && root.contains(element) && predicate(element)
    ? element
    : null
}

// Narrows a host to the common temporal input API.
const isTemporalInput = (element: HTMLElement): element is TemporalInput =>
  'value' in element && 'min' in element && 'max' in element

// Narrows a host to Web Awesome's date-picker API.
const isDatePicker = (element: HTMLElement): element is DatePicker =>
  element.tagName === 'WA-DATE-PICKER' &&
  'value' in element &&
  'min' in element &&
  'max' in element

// Narrows a host to the datetime display API.
const isDateTimeDisplay = (element: HTMLElement): element is DateTimeDisplay =>
  element.tagName === 'WA-INPUT' &&
  'value' in element &&
  'min' in element &&
  'max' in element &&
  'step' in element

// Narrows a host to the datetime popover API.
const isDateTimePopover = (element: HTMLElement): element is DateTimePopover =>
  element.tagName === 'WA-POPOVER' &&
  'anchor' in element &&
  'open' in element &&
  'show' in element &&
  'hide' in element &&
  'updateComplete' in element

// Narrows one related element to a native button.
const isButton = (element: HTMLElement): element is HTMLButtonElement =>
  element instanceof window.HTMLButtonElement

// Narrows one related element to a hidden form carrier.
const isHiddenInput = (element: HTMLElement): element is HTMLInputElement =>
  element instanceof window.HTMLInputElement && element.type === 'hidden'

// Writes a control value only when it changed.
const setValue = (element: ValueElement, value: string): void => {
  if (element.value !== value) {
    element.value = value
  }
}

// Returns midnight in the shape expected by one precision.
const midnightForPrecision = (precision: DateTimePrecision): string =>
  MIDNIGHT_VALUES[precision]

// Writes one constraint only when it changed.
const setConstraint = (
  element: ConstraintElement,
  constraint: 'min' | 'max',
  value: string
): void => {
  if (element[constraint] !== value) {
    element[constraint] = value
  }
}

// Registers one event listener and returns its cleanup.
const listen = (
  target: EventTarget,
  type: string,
  listener: EventListener,
  options?: boolean | AddEventListenerOptions
): Cleanup => {
  target.addEventListener(type, listener, options)
  return () => target.removeEventListener(type, listener, options)
}

// Prevents readonly temporal controls from opening their picker.
const cancelReadonlyShow: EventListener = (event: Event): void => {
  if (
    event.currentTarget instanceof HTMLElement &&
    event.currentTarget.hasAttribute('readonly')
  ) {
    event.preventDefault()
  }
}

// Closes a picker that became disabled or readonly.
const hideBlockedPicker = (input: TemporalInput): void => {
  if (input.disabled || input.readonly) {
    input.hide?.().catch(() => undefined)
  }
}

// Keeps an empty date represented in native form data without duplicating values.
const syncEmptyValueCarrier = (
  input: TemporalInput,
  carrier: HTMLInputElement
): void => {
  carrier.disabled = input.disabled || input.value !== ''
}

// Connects the empty-value carrier to edits and native form resets.
const setupEmptyValueCarrier = (
  input: TemporalInput,
  carrier: HTMLInputElement | null
): DateTimeHookState | null => {
  if (!carrier) {
    return null
  }

  let active = true
  const sync = (): void => syncEmptyValueCarrier(input, carrier)
  const syncAfterReset: EventListener = (): void => {
    queueMicrotask(() => {
      if (active) {
        sync()
      }
    })
  }
  const cleanup = composeCleanup(
    listen(input, 'input', sync),
    listen(input, 'change', sync),
    carrier.form
      ? listen(carrier.form, 'reset', syncAfterReset)
      : () => undefined
  )

  sync()

  return {
    cleanup: (): void => {
      active = false
      cleanup()
    },
    sync
  }
}

// Applies millisecond range and step validity to a time input.
const setMillisecondValidity = (
  input: TemporalInput,
  timeValue: string,
  valueWithinBounds: boolean,
  min: string,
  step: string
): void => {
  const empty = timeValue === ''
  const valid =
    (empty ? !input.required : isTimeStepAligned(timeValue, min, step)) &&
    valueWithinBounds

  input.setCustomValidity?.(valid ? '' : RANGE_ERROR)
}

// Creates the composite datetime controller when all related elements exist.
const setupDateTime = (root: HTMLElement): DateTimeHookState | null => {
  const display = requiredElement(
    root,
    root.dataset.displayId,
    isDateTimeDisplay
  )
  const trigger = requiredElement(root, root.dataset.triggerId, isButton)
  const popover = requiredElement(
    root,
    root.dataset.popoverId,
    isDateTimePopover
  )
  const date = requiredElement(root, root.dataset.dateId, isDatePicker)
  const time = requiredElement(root, root.dataset.timeId, isTemporalInput)
  const hidden = requiredElement(root, root.dataset.inputId, isHiddenInput)

  if (!(display && trigger && popover && date && time && hidden)) {
    return null
  }

  let activePrecision = readPrecision(root)
  let milliseconds = preserveTimeMilliseconds(time.value).milliseconds
  let active = true
  let focusTriggerAfterHide = false

  popover.anchor = display
  popover.updateComplete
    .then(() => {
      if (active) {
        popover.anchor = display
      }
    })
    .catch(() => undefined)

  const precision = (): DateTimePrecision => readPrecision(root)
  const disabled = (): boolean => root.dataset.disabled === 'true'
  const readonly = (): boolean => root.dataset.readonly === 'true'
  const interactive = (): boolean => !(disabled() || readonly())
  const step = (): string => root.dataset.step ?? '60'
  const canonicalTimeValue = (): string => {
    if (precision() !== 'millisecond') {
      return time.value
    }

    const preserved = preserveTimeMilliseconds(time.value, milliseconds)
    milliseconds = preserved.milliseconds
    return preserved.value
  }
  const canonicalControlValue = (): string =>
    combineDateTimeValue(date.value, canonicalTimeValue(), precision())

  const syncRequiredAndValidity = (): void => {
    const required = root.dataset.required === 'true'
    const hasDate = date.value !== ''
    const hasTime = time.value !== ''
    const partial = hasDate !== hasTime
    const value = canonicalControlValue()
    const valueWithinBounds =
      value === '' ||
      isWithinBounds(value, root.dataset.min ?? '', root.dataset.max ?? '')
    const stepAligned =
      !hasTime ||
      isTimeStepAligned(canonicalTimeValue(), root.dataset.min ?? '', step())
    const valid = !partial && valueWithinBounds && stepAligned

    display.required = required
    time.required = false
    display.setCustomValidity?.(valid ? '' : RANGE_ERROR)
    time.setCustomValidity?.('')
  }

  const syncConstraints = (): void => {
    const millisecondPrecision = precision() === 'millisecond'
    const min = root.dataset.min ?? ''
    const max = root.dataset.max ?? ''

    setConstraint(display, 'min', min)
    setConstraint(display, 'max', max)
    display.step = step() === 'any' ? 'any' : Number(step())
    setConstraint(date, 'min', min.split('T')[0] ?? '')
    setConstraint(date, 'max', max.split('T')[0] ?? '')
    setConstraint(
      time,
      'min',
      millisecondPrecision
        ? ''
        : matchingDateTimeBound(date.value, min, precision())
    )
    setConstraint(
      time,
      'max',
      millisecondPrecision
        ? ''
        : matchingDateTimeBound(date.value, max, precision())
    )
  }

  const syncSemanticState = (): void => {
    const isDisabled = disabled()
    const isReadonly = readonly()
    const blocked = isDisabled || isReadonly

    display.disabled = isDisabled
    display.readonly = isReadonly
    display.withClear = !blocked
    trigger.disabled = blocked
    date.disabled = isDisabled
    date.readonly = isReadonly
    time.disabled = isDisabled
    time.readonly = isReadonly
    hidden.disabled = isDisabled

    pipe(
      Array.from(
        root.querySelectorAll<HTMLButtonElement>('[data-datetime-macro]')
      ),
      forEach((button) => {
        button.disabled = blocked
      })
    )

    if (blocked && popover.open) {
      focusTriggerAfterHide = false
      popover.hide().catch(() => undefined)
      time.hide?.().catch(() => undefined)
    }
  }

  const syncControls = (value: string): void => {
    const parts = splitDateTimeValue(value, precision())

    milliseconds = preserveTimeMilliseconds(parts.time).milliseconds
    setValue(date, parts.date)
    setValue(time, parts.time)
    setValue(display, value)
    syncConstraints()
    syncRequiredAndValidity()
  }

  const syncControlsFromDisplay = (): boolean => {
    if (display.value === '') {
      milliseconds = '000'
      setValue(date, '')
      setValue(time, '')
      return true
    }

    const parts = splitDateTimeValue(display.value, precision())

    if (!(parts.date && parts.time)) {
      return false
    }

    milliseconds = preserveTimeMilliseconds(parts.time).milliseconds
    setValue(date, parts.date)
    setValue(time, parts.time)
    return true
  }

  const syncDisplayFromControls = (): void => {
    setValue(display, canonicalControlValue())
  }

  const restoreServerValue = (value: string): void => {
    hidden.value = value
    syncControls(value)
  }

  const sync = (): void => {
    const nextPrecision = precision()

    syncSemanticState()

    if (nextPrecision !== activePrecision) {
      activePrecision = nextPrecision
      milliseconds = preserveTimeMilliseconds(canonicalTimeValue()).milliseconds
    }

    hidden.value = canonicalControlValue()
    syncDisplayFromControls()
    syncConstraints()
    syncRequiredAndValidity()
    trigger.setAttribute('aria-expanded', String(popover.open))
  }

  const applyLiveValue = (values: LivePropValues): void => {
    const value = values.value

    if (typeof value !== 'string') {
      return
    }

    const nextPrecision = precision()

    if (nextPrecision !== activePrecision) {
      activePrecision = nextPrecision
      milliseconds = preserveTimeMilliseconds(
        value.split('T')[1] ?? ''
      ).milliseconds
    }

    syncSemanticState()
    restoreServerValue(value)
    trigger.setAttribute('aria-expanded', String(popover.open))
  }

  const emitCanonicalValue = (): void => {
    const value = canonicalControlValue()

    hidden.value = value
    syncDisplayFromControls()
    syncConstraints()
    syncRequiredAndValidity()
    hidden.dispatchEvent(
      new Event('input', { bubbles: true, cancelable: true })
    )
  }

  const handleDisplayInput: EventListener = (event: Event): void => {
    event.stopPropagation()
    syncControlsFromDisplay()
    syncConstraints()
    syncRequiredAndValidity()
  }

  const handleDisplayChange: EventListener = (event: Event): void => {
    event.stopPropagation()
    if (syncControlsFromDisplay()) {
      emitCanonicalValue()
      return
    }

    syncConstraints()
    syncRequiredAndValidity()
  }

  const handleTemporalInput: EventListener = (event: Event): void => {
    event.stopPropagation()
    syncDisplayFromControls()
    syncConstraints()
    syncRequiredAndValidity()
  }

  const handleTemporalChange: EventListener = (event: Event): void => {
    event.stopPropagation()
    emitCanonicalValue()
  }

  // Picking a date while the time is still empty would combine to '' (a partial
  // datetime is dropped by `combineDateTimeValue`), so the value never persists and the
  // next auto-save round-trip resets the draft. Default the time to midnight so a
  // date-only selection yields a complete datetime the user can then adjust.
  const handleDateChange: EventListener = (event: Event): void => {
    if (date.value !== '' && time.value === '') {
      setValue(time, midnightForPrecision(precision()))
    }

    handleTemporalChange(event)
  }

  const handleMacroClick: EventListener = (event: Event): void => {
    if (
      !(
        event instanceof window.MouseEvent &&
        event.target instanceof window.Element
      )
    ) {
      return
    }

    const button = event.target.closest<HTMLElement>('[data-datetime-macro]')
    const macro = button?.dataset.datetimeMacro

    if (!(interactive() && macro && isDateTimeMacro(macro))) {
      return
    }

    const base = root.dataset.macrosFrom ?? ''
    const value = applyMacro(base, macro, precision())
    const parts = splitDateTimeValue(value, precision())

    milliseconds = preserveTimeMilliseconds(parts.time).milliseconds
    setValue(date, parts.date)
    setValue(time, parts.time)
    emitCanonicalValue()
    focusTriggerAfterHide = true
    popover
      .hide()
      .then(() => {
        if (active && focusTriggerAfterHide && !trigger.disabled) {
          focusTriggerAfterHide = false
          trigger.focus()
        }
      })
      .catch(() => undefined)
  }

  const handlePopoverShow: EventListener = (event: Event): void => {
    if (event.target !== popover) {
      return
    }

    if (!interactive()) {
      event.preventDefault()
      trigger.setAttribute('aria-expanded', 'false')
      return
    }

    trigger.setAttribute('aria-expanded', 'true')
  }

  const handlePopoverAfterShow: EventListener = (event: Event): void => {
    if (event.target !== popover || !active || !popover.open) {
      return
    }

    if (!interactive()) {
      popover.hide().catch(() => undefined)
      return
    }

    // Keep focus where the user moved it while the outer popover animated.
    const focused = root.ownerDocument.activeElement

    if (focused !== popover && focused && popover.contains(focused)) {
      return
    }

    date.focus()
  }

  const handlePopoverAfterHide: EventListener = (event: Event): void => {
    if (event.target !== popover) {
      return
    }

    const anchorReceivedFocus = root.ownerDocument.activeElement === display

    trigger.setAttribute('aria-expanded', 'false')

    if (
      active &&
      (focusTriggerAfterHide || anchorReceivedFocus) &&
      !trigger.disabled
    ) {
      focusTriggerAfterHide = false
      trigger.focus()
    }
  }

  const handleTriggerClick: EventListener = (): void => {
    if (!interactive()) {
      return
    }

    const toggle = popover.open ? popover.hide() : popover.show()

    toggle.catch(() => undefined)
  }

  const handleDisplayKeydown: EventListener = (event: Event): void => {
    if (
      event instanceof window.KeyboardEvent &&
      event.key === 'ArrowDown' &&
      interactive()
    ) {
      event.preventDefault()
      popover.show().catch(() => undefined)
    }
  }

  const handleTimeInvalid: EventListener = (event: Event): void => {
    if (!interactive()) {
      return
    }

    event.preventDefault()
    popover
      .show()
      .then(() => {
        if (!active) {
          return
        }

        if (date.value === '') {
          date.focus()
        } else {
          time.focus()
        }
      })
      .catch(() => undefined)
  }

  const cleanupLiveProps = bindLiveProps(root, {
    read: () => ({ value: canonicalControlValue() }),
    apply: (values) => applyLiveValue(values)
  })

  sync()

  const removeListeners = composeCleanup(
    listen(trigger, 'click', handleTriggerClick),
    listen(display, 'input', handleDisplayInput),
    listen(display, 'change', handleDisplayChange),
    listen(display, 'keydown', handleDisplayKeydown),
    listen(date, 'input', handleTemporalInput),
    listen(time, 'input', handleTemporalInput),
    listen(date, 'change', handleDateChange),
    listen(time, 'change', handleTemporalChange),
    listen(time, 'invalid', handleTimeInvalid),
    listen(time, 'wa-show', cancelReadonlyShow),
    listen(popover, 'wa-show', handlePopoverShow),
    listen(popover, 'wa-after-show', handlePopoverAfterShow),
    listen(popover, 'wa-after-hide', handlePopoverAfterHide),
    listen(root, 'click', handleMacroClick),
    cleanupLiveProps
  )

  return {
    cleanup: () => {
      active = false
      focusTriggerAfterHide = false
      trigger.setAttribute('aria-expanded', 'false')
      removeListeners()
      popover.anchor = null
      popover.hide().catch(() => undefined)
      time.hide?.().catch(() => undefined)
    },
    sync
  }
}

// Creates a controller for a direct date or time custom element.
const setupDirectPicker = (element: HTMLElement): DateTimeHookState | null => {
  if (
    (element.tagName !== 'WA-DATE-INPUT' &&
      element.tagName !== 'WA-TIME-INPUT') ||
    !isTemporalInput(element)
  ) {
    return null
  }

  const carrier =
    element.tagName === 'WA-DATE-INPUT'
      ? requiredElement(element, element.dataset.inputId, isHiddenInput)
      : null
  const carrierState = setupEmptyValueCarrier(element, carrier)
  const sync = (): void => {
    hideBlockedPicker(element)
    carrierState?.sync()
  }
  const cleanupLiveProps = bindLiveProps(element, {
    read: () => ({ value: element.value }),
    apply: (values) => {
      if (isString(values.value)) {
        setValue(element, values.value)
        carrierState?.sync()
      }
    }
  })

  return {
    cleanup: composeCleanup(
      listen(element, 'wa-show', cancelReadonlyShow),
      carrierState?.cleanup ?? (() => undefined),
      cleanupLiveProps,
      () => {
        element.hide?.().catch(() => undefined)
      }
    ),
    sync
  }
}

// Creates the composite time-only controller.
const setupTime = (root: HTMLElement): DateTimeHookState | null => {
  if (root.dataset.component !== '.time-input-field') {
    return null
  }

  const time = requiredElement(root, root.dataset.timeId, isTemporalInput)
  const hidden = requiredElement(root, root.dataset.inputId, isHiddenInput)

  if (!(time && hidden)) {
    return null
  }

  let activePrecision = readPrecision(root)
  let milliseconds = preserveTimeMilliseconds(time.value).milliseconds

  const canonicalValue = (): string => {
    const precision = readPrecision(root)

    if (precision !== 'millisecond') {
      return normalizeTime(time.value, precision) ?? ''
    }

    const preserved = preserveTimeMilliseconds(time.value, milliseconds)
    milliseconds = preserved.milliseconds
    return preserved.value
  }

  const restoreServerValue = (value: string): void => {
    hidden.value = value
    setValue(time, value)
    milliseconds = preserveTimeMilliseconds(value).milliseconds
    syncRangeValidity(value)
  }

  const syncRangeValidity = (value: string): void => {
    const millisecondPrecision = readPrecision(root) === 'millisecond'

    if (!millisecondPrecision) {
      return
    }

    setMillisecondValidity(
      time,
      value,
      value === '' ||
        isTimeWithinBounds(
          value,
          root.dataset.min ?? '',
          root.dataset.max ?? ''
        ),
      root.dataset.min ?? '',
      root.dataset.step ?? '0.001'
    )
  }

  const sync = (): void => {
    const nextPrecision = readPrecision(root)

    hideBlockedPicker(time)

    if (nextPrecision !== activePrecision) {
      activePrecision = nextPrecision
      milliseconds = preserveTimeMilliseconds(canonicalValue()).milliseconds
    }

    hidden.value = canonicalValue()
    syncRangeValidity(hidden.value)
  }

  const stopTemporalInput: EventListener = (event: Event): void => {
    event.stopPropagation()
    syncRangeValidity(canonicalValue())
  }

  const emitCanonicalValue: EventListener = (event: Event): void => {
    event.stopPropagation()
    const value = canonicalValue()

    hidden.value = value
    syncRangeValidity(hidden.value)
    hidden.dispatchEvent(
      new Event('input', { bubbles: true, cancelable: true })
    )
  }

  const cleanupLiveProps = bindLiveProps(root, {
    read: () => ({ value: canonicalValue() }),
    apply: (values) => {
      if (isString(values.value)) {
        restoreServerValue(values.value)
      }
    }
  })

  sync()

  return {
    cleanup: composeCleanup(
      listen(time, 'input', stopTemporalInput),
      listen(time, 'change', emitCanonicalValue),
      listen(time, 'wa-show', cancelReadonlyShow),
      cleanupLiveProps
    ),
    sync
  }
}

// Selects the first controller compatible with this hook host.
const setupTemporalControl = (element: HTMLElement): DateTimeHookState | null =>
  setupDirectPicker(element) ?? setupTime(element) ?? setupDateTime(element)

// Waits for every control before binding a composite's values and listeners.
const mountTemporalControl = (hook: DateTimeHook): void => {
  hook.dateTimeCleanup?.()
  hook.dateTimeCleanup = setupPageComponent(
    hook.el,
    (element) => {
      const state = setupTemporalControl(element)
      hook.dateTimeState = state ?? undefined

      return () => {
        state?.cleanup()
        hook.dateTimeState = undefined
      }
    },
    loadPageComponents
  )
}

// Public functions ------------------------------------------------------------

// Applies one datetime macro with an optional deterministic clock fallback.
export const applyDateTimeMacro = (
  value: string,
  macro: DateTimeMacro,
  precision: DateTimePrecision,
  fallback: Dayjs = dayjs()
): string => applyMacro(value, macro, precision, fallback)

// Public hook -----------------------------------------------------------------

export const FacadeDatepicker = {
  // Connects the first temporal controller compatible with this host.
  mounted(this: DateTimeHook): void {
    mountTemporalControl(this)
  },

  // Reconciles semantic state after a LiveView patch.
  updated(this: DateTimeHook): void {
    if (this.dateTimeState) {
      this.dateTimeState.sync()
    } else {
      mountTemporalControl(this)
    }
  },

  // Releases listeners, adapters, and pending picker work.
  destroyed(this: DateTimeHook): void {
    this.dateTimeCleanup?.()
    this.dateTimeCleanup = undefined
  }
}
